/*
 * Platform-level shaping for the Super Admin page.
 *
 * The markup invented a flat package — `{ seats, retentionDays, storage,
 * clipboard, price }`. The real Package model nests everything under
 * `features` with different names and types, and its price is
 * `{ monthly, yearly, currency }`. Everything here reads the model's shape, so
 * a package created through this page is one the rest of the platform
 * understands.
 *
 * THE ENTITLEMENT IS NOT A PACKAGE FEATURE. `clipboardCaptureAllowed` lives on
 * the ORGANIZATION, not on its package, and is the first of three gates
 * (entitlement → org switch → per-user override). Two organizations on the same
 * package can differ, which is why it is edited per organization rather than
 * implied by the plan.
 */
import { formatNumber } from "@/lib/format.js";

export const TABS = [
    { value: "organizations", label: "Organizations" },
    { value: "users", label: "Users" },
    { value: "analytics", label: "Analytics" },
    { value: "packages", label: "Packages" },
    // App Versions is NOT here: a desktop build is platform-wide, and these
    // tabs are all per-organization views. It has its own Platform menu entry.
];

/* ---- Packages ------------------------------------------------------------ */

const UNLIMITED = 0;

/** The model uses 0 (or a missing value) for "no cap". */
export const limitLabel = (value) =>
    value === UNLIMITED || value == null ? "Unlimited" : formatNumber(value);

export const priceLabel = (pkg) => {
    const monthly = pkg?.price?.monthly;
    if (monthly == null) return "—";
    if (monthly === 0) return "Free";
    const currency = pkg?.price?.currency ?? "USD";
    const symbol = { USD: "$", EUR: "€", GBP: "£", BDT: "৳", INR: "₹" }[currency] ?? "";
    return `${symbol}${formatNumber(monthly)} / month`;
};

/*
 * The boolean features, in the order the model declares them. Rendered as a
 * checklist rather than as switches on the list: this page shows what a plan
 * includes; changing it is the package editor's job.
 */
export const PACKAGE_FLAGS = [
    ["advancedAnalytics", "Advanced analytics"],
    ["customReports", "Custom reports"],
    ["apiAccess", "API access"],
    ["prioritySupport", "Priority support"],
    ["whiteLabel", "White label"],
    ["ssoIntegration", "SSO integration"],
];

export function packageSummary(pkg) {
    const f = pkg?.features ?? {};
    return {
        seats: limitLabel(f.maxUsers),
        projects: limitLabel(f.maxProjects),
        teams: limitLabel(f.maxTeams),
        storage: f.screenshotStorage ?? "—",
        retention: f.dataRetention != null ? `${f.dataRetention} days` : "—",
        flags: PACKAGE_FLAGS.filter(([key]) => f[key]).map(([, label]) => label),
    };
}

/* ---- Organizations ------------------------------------------------------- */

/**
 * One row per organization, from the aggregate getAllOrganizations returns.
 *
 * `overSeat` is the number that matters on this page: a plan sold with a seat
 * cap that the organization has quietly exceeded. It is computed rather than
 * stored, and only when the package HAS a cap — an unlimited plan can never be
 * over it, and treating the model's 0 as a limit would mark every enterprise
 * organization as in breach.
 */
export function organizationRows(organizations = []) {
    return organizations.map((o) => {
        const cap = o.package?.features?.maxUsers;
        const capped = typeof cap === "number" && cap > 0;

        return {
            id: o._id ?? o.id,
            name: o.name,
            identifier: o.identifier,
            isActive: o.isActive !== false,
            createdAt: o.createdAt,
            lastActivity: o.lastActivity ?? null,
            packageName: o.package?.name ?? "No package",
            packageId: o.package?._id ?? o.package ?? null,
            seatCap: capped ? cap : null,
            totalUsers: o.totalUsers ?? 0,
            activeUsers: o.activeUsers ?? 0,
            totalTimeEntries: o.totalTimeEntries ?? 0,
            overSeat: capped && (o.totalUsers ?? 0) > cap,
            seatPercent: capped ? Math.min(100, ((o.totalUsers ?? 0) / cap) * 100) : 0,
            // The API sends a flag, not the credentials — see the projection in
            // superAdminController.getAllOrganizations.
            ownBucket: Boolean(o.hasOwnBucket),
            clipboardAllowed: Boolean(o.clipboardCaptureAllowed),
            trackMode: o.settings?.trackMode ?? "regular",
            phone: o.phone ?? "",
            country: o.country ?? "",
            website: o.website ?? "",
            address: o.address ?? "",
        };
    });
}

export function platformStats(rows, overview) {
    const active = rows.filter((r) => r.isActive).length;
    const overSeat = rows.filter((r) => r.overSeat).length;

    return {
        organizations: overview?.totalOrganizations ?? rows.length,
        activeOrganizations: overview?.activeOrganizations ?? active,
        suspended: (overview?.totalOrganizations ?? rows.length) - (overview?.activeOrganizations ?? active),
        users: overview?.totalUsers ?? rows.reduce((sum, r) => sum + r.totalUsers, 0),
        activeUsers: overview?.activeUsers ?? rows.reduce((sum, r) => sum + r.activeUsers, 0),
        recentTimeEntries: overview?.recentTimeEntries ?? null,
        overSeat,
    };
}

export function filterOrganizations(rows, { search = "", status = "" }) {
    const q = search.trim().toLowerCase();

    return rows.filter((r) => {
        if (status === "active" && !r.isActive) return false;
        if (status === "suspended" && r.isActive) return false;
        if (status === "overSeat" && !r.overSeat) return false;
        if (q && !`${r.name} ${r.identifier}`.toLowerCase().includes(q)) return false;
        return true;
    });
}

export const ORG_STATUS_FILTERS = [
    { value: "", label: "All organizations" },
    { value: "active", label: "Active" },
    { value: "suspended", label: "Suspended" },
    { value: "overSeat", label: "Over seat limit" },
];

/* ---- Cross-organization users -------------------------------------------- */

export function userRows(users = []) {
    return users.map((u) => ({
        id: u._id ?? u.id,
        name: u.name,
        email: u.email,
        role: u.role,
        isActive: u.isActive !== false,
        lastActive: u.lastActive ?? null,
        organizationName:
            typeof u.organization === "object" ? (u.organization?.name ?? "—") : "—",
        organizationId:
            typeof u.organization === "object" ? u.organization?._id : (u.organization ?? null),
    }));
}

export const ROLE_FILTERS = [
    { value: "", label: "All roles" },
    { value: "superAdmin", label: "Super Admin" },
    { value: "admin", label: "Admin" },
    { value: "manager", label: "Manager" },
    { value: "employee", label: "Employee" },
];

/* ---- Analytics ----------------------------------------------------------- */

/*
 * getSystemAnalytics returns three aggregates keyed by a { _id: { year, month
 * … } } group. They are turned into plain label/value points here so the chart
 * components stay ignorant of Mongo's grouping shape.
 */
export function growthSeries(points = []) {
    return points.map((p) => {
        const id = p._id ?? {};
        const label =
            id.year && id.month
                ? `${String(id.month).padStart(2, "0")}/${String(id.year).slice(2)}`
                : String(id.date ?? id.day ?? "");
        return { label, value: p.count ?? p.total ?? 0 };
    });
}

export function activitySeries(rows = []) {
    return rows
        .map((r) => ({
            label: r.organizationName ?? r.name ?? r._id ?? "—",
            value: r.count ?? r.timeEntries ?? 0,
        }))
        .sort((a, b) => b.value - a.value);
}
